import { useEffect, useState } from "react";
import { api, errMsg, openPdf } from "../lib/api";
import { fmtMoney, fmtDateTime, Badge } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { DataTable } from "../components/DataTable";
import { Modal, Field, inputCls, selectCls, btnPrimary, btnSecondary, btnDanger } from "../components/Modal";
import { toast } from "sonner";
import { Plus, Printer, Trash2, ArrowRight, FileOutput } from "lucide-react";
import { salePriceFor, unitOptionLabel } from "../lib/units";

const TYPES = { quotation: "Devis", proforma: "Facture pro forma", invoice: "Facture", delivery: "Bon de livraison" };

export default function Documents() {
  const { user, hasPerm } = useAuth();
  const cur = user?.currency || "FCFA";
  const [type, setType] = useState("quotation");
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [form, setForm] = useState({ customer_id: "", discount: "", note: "", valid_days: 15, items: [] });

  const load = () => api.get("/documents", { params: { type } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [type]);
  useEffect(() => {
    api.get("/products", { params: { status: "active", limit: 2000 } }).then((r) => setProducts(r.data.items)).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  const addItem = () => setForm({ ...form, items: [...form.items, { product_id: "", qty: 1, unit: "", unit_price: null, discount: "" }] });
  const customer = customers.find((c) => c.id === form.customer_id);
  const total = form.items.reduce((s, i) => {
    const p = products.find((x) => x.id === i.product_id);
    const price = i.unit_price != null && i.unit_price !== "" ? Number(i.unit_price) : (p ? salePriceFor(p, i.unit, customer) : 0);
    return s + price * Number(i.qty || 0) - Number(i.discount || 0);
  }, 0) - Number(form.discount || 0);

  const save = async () => {
    try {
      await api.post("/documents", { type, customer_id: form.customer_id, discount: Number(form.discount) || 0,
        note: form.note, valid_days: Number(form.valid_days) || 15,
        items: form.items.map((i) => ({ product_id: i.product_id, qty: Number(i.qty), unit: i.unit || "", unit_price: i.unit_price === "" || i.unit_price == null ? null : Number(i.unit_price), discount: Number(i.discount) || 0 })) });
      toast.success(`${TYPES[type]} créé`);
      setOpen(false);
      setForm({ customer_id: "", discount: "", note: "", valid_days: 15, items: [] });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const convert = async (d) => {
    try {
      const r = await api.post(`/documents/${d.id}/convert`);
      toast.success(`Converti en vente ${r.data.number} — stock déduit`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async () => {
    try {
      await api.delete(`/documents/${confirmDel.id}`);
      toast.success("Document supprimé");
      setConfirmDel(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const columns = [
    { key: "number", label: "N°", render: (d) => <span className="font-money font-bold">{d.number}</span> },
    { key: "created_at", label: "Date", render: (d) => fmtDateTime(d.created_at) },
    { key: "customer_name", label: "Client" },
    { key: "total", label: "Total", className: "text-right", render: (d) => <span className="font-money font-bold">{fmtMoney(d.total, cur)}</span> },
    { key: "status", label: "Statut", render: (d) => <Badge status={d.status} /> },
    { key: "actions", label: "", className: "text-right", render: (d) => (
      <div className="flex justify-end gap-1">
        <button data-testid={`pdf-doc-${d.id}`} onClick={() => openPdf(`/documents/${d.id}/pdf`)} title="PDF" className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-muted"><Printer className="h-4 w-4" /></button>
        {d.type === "quotation" && !d.converted_sale_id && hasPerm("documents.create") && (
          <button data-testid={`convert-doc-${d.id}`} onClick={() => convert(d)} title="Convertir en vente" className="h-8 px-2 inline-flex items-center gap-1 rounded-md text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950 text-xs font-bold"><FileOutput className="h-4 w-4" /> Vendre</button>)}
        {hasPerm("documents.delete") && !d.converted_sale_id && <button data-testid={`delete-doc-${d.id}`} onClick={() => setConfirmDel(d)} className="h-8 w-8 inline-flex items-center justify-center rounded-md text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950"><Trash2 className="h-4 w-4" /></button>}
      </div>) },
  ];

  return (
    <div className="space-y-4" data-testid="documents-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Devis & Factures</h1>
        {hasPerm("documents.create") && <button data-testid="add-document-btn" onClick={() => setOpen(true)} className={btnPrimary}><Plus className="h-4 w-4" /> Nouveau {TYPES[type].toLowerCase()}</button>}
      </div>
      <div className="flex gap-2 flex-wrap">
        {Object.entries(TYPES).map(([k, l]) => (
          <button key={k} data-testid={`doc-tab-${k}`} onClick={() => setType(k)}
            className={`px-4 h-10 rounded-lg text-sm font-semibold transition-colors ${type === k ? "bg-primary text-primary-foreground" : "bg-card border hover:bg-muted"}`}>{l}</button>
        ))}
      </div>
      <DataTable testid="documents-table" columns={columns} rows={rows} searchKeys={["number", "customer_name"]} />

      <Modal open={open} onClose={() => setOpen(false)} title={`Nouveau : ${TYPES[type]}`} wide>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Client">
            <select data-testid="doc-customer" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} className={selectCls}>
              <option value="">Client comptant</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Validité (jours)"><input data-testid="doc-validity" type="number" min="1" value={form.valid_days} onChange={(e) => setForm({ ...form, valid_days: e.target.value })} className={inputCls} /></Field>
        </div>
        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Articles</span>
            <button data-testid="doc-add-item" onClick={addItem} className="text-xs text-primary font-semibold hover:underline">+ Article</button>
          </div>
          {form.items.map((it, i) => {
            const p = products.find((x) => x.id === it.product_id);
            return (
            <div key={i} className="flex gap-2 items-center flex-wrap">
              <select data-testid={`doc-item-${i}`} value={it.product_id} onChange={(e) => setForm({ ...form, items: form.items.map((x, j) => j === i ? { ...x, product_id: e.target.value, unit: "", unit_price: null } : x) })} className={`${selectCls} flex-1`}>
                <option value="">— Produit —</option>
                {products.map((p) => <option key={p.id} value={p.id}>{`${p.name} — ${fmtMoney(p.sale_price, cur)}`}</option>)}
              </select>
              {(p?.units || []).length > 0 && (
                <select data-testid={`doc-unit-${i}`} value={it.unit || ""}
                  onChange={(e) => setForm({ ...form, items: form.items.map((x, j) => j === i ? { ...x, unit: e.target.value, unit_price: null } : x) })}
                  className={`${selectCls} w-40`}>
                  <option value="">{p.unit} — {fmtMoney(salePriceFor(p, "", customer), cur)}</option>
                  {p.units.filter((u) => u.sellable !== false).map((u) => <option key={u.unit} value={u.unit}>{unitOptionLabel(p.unit, u, salePriceFor(p, u.unit, customer), cur)}</option>)}
                </select>
              )}
              <input data-testid={`doc-qty-${i}`} type="number" min="0.01" step="any" value={it.qty} onChange={(e) => setForm({ ...form, items: form.items.map((x, j) => j === i ? { ...x, qty: e.target.value } : x) })} className={`${inputCls} w-20`} />
              <button onClick={() => setForm({ ...form, items: form.items.filter((_, j) => j !== i) })} className="text-rose-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ); })}
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Field label="Remise"><input data-testid="doc-discount" type="number" min="0" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} className={inputCls} /></Field>
          <Field label="Note"><input data-testid="doc-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inputCls} /></Field>
        </div>
        <div className="flex items-center justify-between font-heading font-bold text-lg mt-3">
          <span>Total</span><span data-testid="doc-total" className="font-money text-primary">{fmtMoney(total, cur)}</span>
        </div>
        <button data-testid="doc-submit" onClick={save} disabled={form.items.length === 0 || form.items.some((i) => !i.product_id)} className={`${btnPrimary} w-full mt-2`}>
          Créer <ArrowRight className="h-4 w-4" />
        </button>
      </Modal>

      <Modal open={!!confirmDel} onClose={() => setConfirmDel(null)} title="Supprimer le document">
        <p className="text-sm">Supprimer <b>{confirmDel?.number}</b> ?</p>
        <div className="flex gap-2 mt-4">
          <button data-testid="confirm-delete-doc" onClick={del} className={`${btnDanger} flex-1`}>Supprimer</button>
          <button onClick={() => setConfirmDel(null)} className={btnSecondary}>Annuler</button>
        </div>
      </Modal>
    </div>
  );
}
